// hooks/useStoredDecibelAverage.ts
import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"

export const useStoredDecibelAverage = () => {
    const { sonometerId } = useParams<{ sonometerId: string }>()
    const screenId = sonometerId || "live-sound-display"
    const [storedAverage, setStoredAverage] = useState<number | null>(null)
    const [maxAverage, setMaxAverage] = useState<number | null>(null)

    useEffect(() => {
        const key = `sonometer-data-${screenId}`

        // Function to load values from localStorage
        const loadValues = () => {
            const dataStr = localStorage.getItem(key)
            if (dataStr) {
                try {
                    const data = JSON.parse(dataStr)
                    setStoredAverage(data.currentScore)
                    setMaxAverage(parseFloat(data.maxScore.toFixed(3)))
                } catch {
                    // Handle old format for backward compatibility
                    const liveKey = `decibel-average-live-${screenId}`
                    const maxKey = `decibel-average-max-${screenId}`

                    const liveAvg = localStorage.getItem(liveKey)
                    if (liveAvg) {
                        setStoredAverage(parseFloat(liveAvg))
                    }

                    const maxAvg = localStorage.getItem(maxKey)
                    if (maxAvg) {
                        setMaxAverage(parseFloat(parseFloat(maxAvg).toFixed(3)))
                    }
                }
            }
        }

        // Load initially
        loadValues()

        // Listen for custom event dispatched when localStorage is updated
        const handleStorageUpdate = (e: CustomEvent) => {
            if (e.detail.key === key) {
                const data = e.detail.value
                setStoredAverage(data.currentScore)
                setMaxAverage(parseFloat(data.maxScore.toFixed(3)))
            }
        }

        window.addEventListener(
            "localStorageUpdated" as any,
            handleStorageUpdate
        )

        return () => {
            window.removeEventListener(
                "localStorageUpdated" as any,
                handleStorageUpdate
            )
        }
    }, [screenId])

    const clearStoredAverage = () => {
        const key = `sonometer-data-${screenId}`
        localStorage.removeItem(key)
        // Also clear old format keys for cleanup
        localStorage.removeItem(`decibel-average-live-${screenId}`)
        localStorage.removeItem(`decibel-average-max-${screenId}`)
        setStoredAverage(null)
        setMaxAverage(null)
    }

    return {
        storedAverage,
        maxAverage,
        clearStoredAverage,
    }
}
